import React, { ReactNode, useState } from "react";
import styled from "styled-components";
import ScheduleEditComponent from "./ScheduleEditComponent";
import Text from "@lib/microComponents/Text";
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DndContext,
  closestCenter,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { v4 as uuid } from "uuid";
import { Formik } from "./main";
import { ScheduleItemType } from "@components/AdminDashboard/SubPages/Courses/AddCourse/formInitialValues";
import { SCHEDULE } from "@components/AdminDashboard/SubPages/Courses/AddCourse/validationSchema";

// styled-components----
const ScheduleContainer = styled.div`
  margin-top: 1.25rem;
`;
const ScheduleSubHeadingContainer = styled.div`
  display: grid;
  grid-template-columns:
    minmax(0, 2fr)
    minmax(0, 1fr)
    minmax(0, 1fr)
    minmax(0, 1fr);
  column-gap: 1.9375rem;
  margin-top: 1rem;
`;
const FormSubHeading = styled.div``;
const ScheduleItemsContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 2.5rem;
  margin-top: 1.25rem;
`;
const AddScheduleItemButtonContainer = styled.div`
  display: flex;
  margin-top: 1.25rem;
  justify-content: flex-end;
`;
const AddScheduleItemButton = styled.button`
  height: 43px;
  width: 43px;
  font-size: 18px;
  background: ${(props) => props.theme.colors.gray[600]};
  color: #ffffff;
  border-radius: 10px;
`;
const Heading = styled.div``;
const ButtonContainer = styled.div`
  margin-top: 4.6875rem;
  display: flex;
  justify-content: center;
`;
interface ButtonProps {
  $loading: boolean;
}
const EditScheduleButton = styled.button<ButtonProps>`
  position: relative;
  font-size: 1.125rem;
  line-height: 1.5625rem;
  width: 35.75rem;
  height: 3.8125rem;
  border-radius: 10px;
  font-weight: 700;
  color: #ffffff;
  background: ${(props) => props.theme.colors.green[600]};
  ${(props) =>
    props.$loading &&
    `
    &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 10px;
    background: #ffffff;
    opacity: 0.85;
  }
  `}
`;
//----------------------

function DndContextProvider({
  children,

  formik,
}: {
  children: ReactNode;
  formik: Formik;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  function findIndexOfAnElement(items: ScheduleItemType[], id: string): number {
    for (let i = 0; i < items.length; i++) {
      if (items[i].id === id) {
        return i;
      }
    }
    return -1;
  }
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      const items = formik.getFieldProps(SCHEDULE).value;
      const oldIndex = findIndexOfAnElement(items, active.id.toString());
      const newIndex = findIndexOfAnElement(items, over.id.toString());
      const newItems = arrayMove(items, oldIndex, newIndex);
      formik.setFieldValue(SCHEDULE, newItems);
    }
  }
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={formik.getFieldProps(SCHEDULE).value}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
}

function ScheduleForm({ formik }: { formik: Formik }) {
  const increaseArrayItems = () => {
    const values = formik.getFieldProps(SCHEDULE).value;
    const newValue: ScheduleItemType = {
      name: `Training ${values.length + 1}`,
      date: undefined,
      startTime: undefined,
      endTime: undefined,
      id: uuid(),
    };

    formik.setFieldValue(SCHEDULE, [...values, newValue]);
  };
  const deleteAScheduleItem = (id: string) => {
    const values: ScheduleItemType[] = formik.getFieldProps(SCHEDULE).value;
    const newValues: ScheduleItemType[] = [];
    if (values.length > 1) {
      for (let i = 0; i < values.length; i++) {
        const value = values[i];
        if (id !== value.id) {
          newValues.push({
            name: value.name,
            date: value.date,
            startTime: value.startTime,
            endTime: value.endTime,
            id: value.id,
          });
        }
      }
      formik.setFieldValue(SCHEDULE, newValues);
    }
  };
  return (
    <ScheduleContainer>
      <ScheduleSubHeadingContainer>
        <div></div>
        <FormSubHeading>
          <Text>Date</Text>
        </FormSubHeading>
        <FormSubHeading>
          <Text>Starting Time</Text>
        </FormSubHeading>
        <FormSubHeading>
          <Text>Ending Time</Text>
        </FormSubHeading>
      </ScheduleSubHeadingContainer>
      <DndContextProvider formik={formik}>
        <ScheduleItemsContainer>
          {formik
            .getFieldProps(SCHEDULE)
            .value.map((e: ScheduleItemType, index: number) => {
              return (
                <ScheduleEditComponent
                  deleteItem={deleteAScheduleItem}
                  key={e.id}
                  id={e.id}
                  formik={formik}
                  index={index}
                />
              );
            })}
        </ScheduleItemsContainer>
      </DndContextProvider>
      <AddScheduleItemButtonContainer>
        <AddScheduleItemButton onClick={increaseArrayItems} type="button">
          +
        </AddScheduleItemButton>
      </AddScheduleItemButtonContainer>
      <ButtonContainer>
        <EditScheduleButton
          $loading={formik.isSubmitting}
          onClick={() => {
            formik.submitForm();
          }}
        >
          Publish schedule
        </EditScheduleButton>
      </ButtonContainer>
    </ScheduleContainer>
  );
}

export default ScheduleForm;
